import React from "react";
import LanguageLink from "../routing/LanguageLink";
import { motion } from "framer-motion";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import manJailImage from "../../assets/mig1.jpg";
import { useTranslation } from "react-i18next";

const WhyChooseUsSection = () => {
  const { t } = useTranslation();

  const featuresData = [
    {
      id: 1,
      icon: "dollar",
      color: "from-green-500 to-emerald-600",
      bgColor: "bg-green-50",
      iconColor: "text-green-600",
    },
    {
      id: 2,
      icon: "clock",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      id: 3,
      icon: "shield",
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      iconColor: "text-purple-600",
    },
    {
      id: 4,
      icon: "handshake",
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      iconColor: "text-orange-600",
    },
    {
      id: 5,
      icon: "certificate",
      color: "from-indigo-500 to-indigo-600",
      bgColor: "bg-indigo-50",
      iconColor: "text-indigo-600",
    },
    {
      id: 6,
      icon: "phone",
      color: "from-pink-500 to-pink-600",
      bgColor: "bg-pink-50",
      iconColor: "text-pink-600",
    },
  ];

  const data = {
    title: t("why_choose_us.title"),
    subtitle: t("why_choose_us.subtitle"),
    features: featuresData.map((f, i) => ({
      ...f,
      title: t(`why_choose_us.items.${i}.title`),
      description: t(`why_choose_us.items.${i}.description`)
    })),
    highlightedCard: {
      title: t("why_choose_us.highlight_card.title"),
      description: t("why_choose_us.highlight_card.description"),
      buttonText: t("why_choose_us.highlight_card.button"),
      badgeTitle: t("why_choose_us.highlight_card.badge_title"),
      badgeSubtitle: t("why_choose_us.highlight_card.badge_subtitle")
    }
  };

  const getIcon = (iconType) => {
    switch (iconType) {
      case "dollar":
        return (
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">
            $
          </div>
        );
      case "clock":
        return (
          <svg
            className="w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="9" strokeWidth={1.5} />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 7v6l4 2"
            />
          </svg>
        );
      case "shield":
        return (
          <svg
            className="w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 3l7 4v6c0 4.418-3.582 6.873-7 8-3.418-1.127-7-3.582-7-8V7l7-4z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12l2 2 4-4"
            />
          </svg>
        );
      case "handshake":
        return (
          <svg
            className="w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 12l3 3a2 2 0 002 0l3-3m-9 0l-2-2a3 3 0 010-4l1-1a3 3 0 014 0l1 1m6 6l2-2a3 3 0 000-4l-1-1a3 3 0 00-4 0l-1 1"
            />
          </svg>
        );
      case "certificate":
        return (
          <svg
            className="w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 2a7 7 0 100 14 7 7 0 000-14z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8.5 16l-2 5 3.5-2 3.5 2-2-5m4.5 0l2 5-3.5-2-3.5 2 2-5"
            />
            <circle cx="12" cy="9" r="3" strokeWidth={1.5} />
          </svg>
        );
      case "phone":
        return (
          <svg
            className="w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 5a2 2 0 012-2h2l2 5-2 1a11 11 0 005 5l1-2 5 2v2a2 2 0 01-2 2h-1C9.82 18.5 5.5 14.18 4 9V8a3 3 0 01-1-3z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <section className="relative bg-gradient-to-b from-gray-50 to-white py-16 lg:py-20 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 -right-20 w-72 h-72 bg-blue-200 rounded-full opacity-20 blur-3xl"
          animate={{
            x: [0, 30, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute -bottom-20 -left-20 w-96 h-96 bg-purple-200 rounded-full opacity-20 blur-3xl"
          animate={{
            x: [0, -40, 0],
            y: [0, -30, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-block"
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Why Choose Us
            </span>
          </motion.div>

          <motion.h2
            className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            {data.title}
          </motion.h2>

          <motion.p
            className="text-xl text-gray-600"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            {data.subtitle}
          </motion.p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {data.features.map((feature, index) => (
            <ScrollAnimation
              key={feature.id}
              direction="up"
              delay={index * 0.05}
            >
              <motion.div
                className="group bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 hover:border-blue-400 transition-all duration-300 h-full"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{
                  y: -10,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                }}
              >
                {/* Icon Container with Gradient */}
                <motion.div
                  className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 shadow-lg`}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="text-white">{getIcon(feature.icon)}</div>
                </motion.div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            </ScrollAnimation>
          ))}
        </div>

        {/* CTA Card */}
        <ScrollAnimation direction="up" delay={0.3}>
          <motion.div
            className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-3xl p-8 lg:p-12 shadow-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Animated Background Shapes */}
            <motion.div
              className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full opacity-10"
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0],
              }}
              transition={{ duration: 10, repeat: Infinity }}
            />
            <motion.div
              className="absolute -bottom-10 -left-10 w-72 h-72 bg-purple-500 rounded-full opacity-20"
              animate={{
                scale: [1, 1.3, 1],
                rotate: [0, -90, 0],
              }}
              transition={{ duration: 15, repeat: Infinity }}
            />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <motion.h3
                  className="text-3xl lg:text-4xl font-bold text-white mb-4"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  {data.highlightedCard.title}
                </motion.h3>
                <motion.p
                  className="text-blue-100 text-lg leading-relaxed mb-6"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  {data.highlightedCard.description}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <LanguageLink
                    to="/forms"
                    className="inline-flex items-center bg-white text-blue-600 px-8 py-3 rounded-full font-bold hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    {data.highlightedCard.buttonText}
                    <motion.svg
                      className="w-5 h-5 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </motion.svg>
                  </LanguageLink>
                </motion.div>
              </div>

              <motion.div
                className="relative"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={manJailImage}
                    alt="USCIS form assistance and attorney handoff"
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                </div>
                {/* Floating Badge */}
                <motion.div
                  className="absolute -top-4 -right-4 bg-white rounded-full p-4 shadow-xl"
                  animate={{
                    y: [0, -10, 0],
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="text-center">
                    <div className="text-sm font-bold text-blue-600">
                      On-Demand
                    </div>
                    <div className="text-xs text-gray-600 font-semibold">
                      Attorney Review
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </ScrollAnimation>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
